import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import { IconButton, InputAdornment } from '@mui/material';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
// components
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { useBoolean } from 'src/hooks/use-boolean';
import { useAuthContext } from 'src/auth/hooks';
import { updateUserProfile } from 'src/api/profile'; // Assuming this API call is still relevant
import { convertErrorsToArray } from 'src/utils/flatten-array'; // Assuming this utility is still relevant

// ----------------------------------------------------------------------

export default function UserNewEditForm() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const password = useBoolean();
  const { user, refreshUser } = useAuthContext();

  const NewUserSchema = Yup.object().shape({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
    password: Yup.string()
      .nullable()
      .notRequired()
      .test(
        'is-valid-password',
        'Password must be at least 8 characters long, include at least one uppercase letter, and one special character',
        (value) => {
          if (!value) return true; // Skip validation if the field is empty or null
          const hasMinLength = value.length >= 8;
          const hasUppercase = /[A-Z]/.test(value);
          const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
          return hasMinLength && hasUppercase && hasSpecialChar;
        }
      ),
    phoneNumber: Yup.string()
      .required('Phone number is required')
      .test('is-correct-length', 'Phone number must have exactly 10 digits', (value) => {
        if (!value) return false;
        const digitsOnly = value.replace(/\D/g, '');
        return digitsOnly.length === 11;
      }),
    office: Yup.string().notRequired().nullable(), // Assuming 'office' can be an array of strings
    city: Yup.string().notRequired().nullable(),
    zipCode: Yup.string().notRequired().nullable(),
    street: Yup.string().notRequired().nullable(),
  });

  const defaultValues = useMemo(
    () => ({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      userName: user?.userName || '',
      phoneNumber: user?.phoneNo || '',
      city: user?.city || '',
      street: user?.street || '',
      zipCode: user?.zipCode || '',
      email: user?.email || '',
      password: '',
      office: user?.office?.[0]?.name || [], // Initialize as an empty array if not present
    }),
    [user]
  );

  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmit = handleSubmit(async (value) => {
    setLoading(true);
    // const apiData = {
    //   name: value?.name,
    // };

    const formData2 = {
      firstName: value.firstName,
      lastName: value.lastName,
      userName: value.userName,
      email: value.email,
      officeId: user?.offices[0]?._id,
      phoneNo: value.phoneNumber,
      city: value.city,
      street: value.street,
      zipCode: value.zipCode,
      // ...(value.password && { password: value.password })
    };

    try {
      const data = await updateUserProfile(formData2);
      if (data.code === 200) {
        refreshUser();
        enqueueSnackbar(data.message);
        reset(value);
        router.push(paths.dashboard.root);
      }
    } catch (error) {
      console.error(error);
      const errorsArray = convertErrorsToArray(error.data);
      if (errorsArray.length > 0) {
        errorsArray.forEach((err) => enqueueSnackbar(err, { variant: 'error' }));
      } else if (error.message) {
        enqueueSnackbar(error.message, { variant: 'error' });
      } else {
        enqueueSnackbar('Internal Server Error', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card sx={{ p: 3 }}>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <RHFTextField name="firstName" label="First Name" />
              <RHFTextField name="lastName" label="Last Name" />
              <RHFTextField name="userName" label="Username" disabled />
              <RHFTextField name="email" label="Email" disabled />
              <RHFTextField name="office" label="Office" disabled />
              <RHFTextField name="phoneNumber" label="Phone Number" />
              <RHFTextField name="city" label="City" />
              <RHFTextField name="street" label="Street" />
              <RHFTextField name="zipCode" label="Zip Code" />

              {/*<Box>*/}
              {/*  <RHFTextField*/}
              {/*    name="password"*/}
              {/*    label="Password"*/}
              {/*    type={password.value ? 'text' : 'password'}*/}
              {/*    InputProps={{*/}
              {/*      endAdornment: (*/}
              {/*        <InputAdornment position="end">*/}
              {/*          <IconButton onClick={password.onToggle} edge="end">*/}
              {/*            <Iconify*/}
              {/*              icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}*/}
              {/*            />*/}
              {/*          </IconButton>*/}
              {/*        </InputAdornment>*/}
              {/*      ),*/}
              {/*    }}*/}
              {/*  />*/}
              {/*  {!errors.password && (*/}
              {/*    <Typography*/}
              {/*      variant="body2"*/}
              {/*      style={{ fontSize: '11px', margin: '0px', marginTop: '2px' }}*/}
              {/*    >*/}
              {/*      Password must be at least 8 characters long, include one uppercase letter, and*/}
              {/*      contain one special character.*/}
              {/*    </Typography>*/}
              {/*  )}*/}
              {/*</Box>*/}

              {/* Assuming 'office' is now a multi-select or tag input for multiple offices */}
              {/* You'll need to replace RHFTextField with a component that supports multiple values */}
            </Box>

            <Stack alignItems="flex-end" sx={{ mt: 3 }}>
              <LoadingButton type="submit" variant="contained" loading={loading}>
                Update Profile
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}

UserNewEditForm.propTypes = {
  currentUser: PropTypes.object,
};
